import {
  useEffect,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

import { usePermissions } from "./PermissionContext";


const API_URL = (
  process.env.REACT_APP_API_URL ||
  "/api"
).replace(/\/+$/, "");

const EMPTY_FORM = {
  electronic_connection_mode: "not_configured",
  electronic_environment: "test",
  electronic_provider_name: "",
};


function getHeaders() {
  const token = localStorage.getItem("company_token");

  return token
    ? { Authorization: `Bearer ${token}` }
    : {};
}


function errorText(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return error.message || "No fue posible guardar la configuración.";
}


function toForm(data) {
  return {
    electronic_connection_mode:
      data.electronic_connection_mode || "not_configured",

    electronic_environment:
      data.electronic_environment || "test",

    electronic_provider_name:
      data.electronic_provider_name || "",
  };
}


export default function CompanyElectronicConnection() {
  const { can } = usePermissions();

  const canView = can("settings.view");
  const canManage = can("settings.manage");

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submittingRef = useRef(false);
  const mountedRef = useRef(false);
  const controllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    const controller = new AbortController();

    if (!canView) {
      setLoading(false);

      return () => {
        mountedRef.current = false;
        controllerRef.current?.abort();
      };
    }

    setLoading(true);
    setError("");

    axios
      .get(`${API_URL}/settings/invoice/me`, {
        headers: getHeaders(),
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setForm(toForm(data));
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(errorText(requestError));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      mountedRef.current = false;
      controller.abort();
      controllerRef.current?.abort();
    };
  }, [canView]);

  function handleChange(event) {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (
      !canView ||
      !canManage ||
      loading ||
      submittingRef.current
    ) {
      return;
    }

    const providerName =
      form.electronic_provider_name.trim();

    if (
      form.electronic_connection_mode === "provider" &&
      !providerName
    ) {
      setError("Indica el nombre del proveedor tecnológico.");
      return;
    }

    submittingRef.current = true;
    setSaving(true);
    setError("");

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const { data } = await axios.put(
        `${API_URL}/settings/invoice/me`,
        {
          electronic_connection_mode:
            form.electronic_connection_mode,

          electronic_environment:
            form.electronic_environment,

          electronic_provider_name:
            form.electronic_connection_mode === "provider"
              ? providerName
              : null,
        },
        {
          headers: getHeaders(),
          signal: controller.signal,
        }
      );

      if (!mountedRef.current || controller.signal.aborted) {
        return;
      }

      setForm(toForm(data));

      toast.success(
        "Preferencias de conexión electrónica guardadas."
      );
    } catch (requestError) {
      if (mountedRef.current && !controller.signal.aborted) {
        setError(errorText(requestError));
      }
    } finally {
      submittingRef.current = false;

      if (mountedRef.current && !controller.signal.aborted) {
        setSaving(false);
      }
    }
  }

  if (!canView) return null;

  return (
    <section className="billing-card p-3 p-lg-4 mb-4">
      <h2 className="h5">
        Conexión de facturación electrónica
      </h2>

      <p className="text-muted">
        Define cómo se conectará esta empresa para transmitir
        sus documentos electrónicos.
      </p>

      {loading ? (
        <p role="status">Consultando configuración…</p>
      ) : (
        <form onSubmit={handleSubmit} aria-busy={saving}>
          <fieldset disabled={!canManage || saving}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label
                  htmlFor="electronic_connection_mode"
                  className="form-label"
                >
                  Modo de conexión
                </label>

                <select
                  id="electronic_connection_mode"
                  name="electronic_connection_mode"
                  className="form-select"
                  value={form.electronic_connection_mode}
                  onChange={handleChange}
                >
                  <option value="not_configured">
                    Sin configurar
                  </option>

                  <option value="provider">
                    Proveedor tecnológico
                  </option>

                  <option value="direct_dian">
                    Conexión directa con la DIAN
                  </option>
                </select>
              </div>

              <div className="col-12 col-md-4">
                <label
                  htmlFor="electronic_environment"
                  className="form-label"
                >
                  Ambiente
                </label>

                <select
                  id="electronic_environment"
                  name="electronic_environment"
                  className="form-select"
                  value={form.electronic_environment}
                  onChange={handleChange}
                >
                  <option value="test">Pruebas</option>
                  <option value="production">Producción</option>
                </select>
              </div>

              {form.electronic_connection_mode === "provider" && (
                <div className="col-12 col-md-4">
                  <label
                    htmlFor="electronic_provider_name"
                    className="form-label"
                  >
                    Nombre del proveedor
                  </label>

                  <input
                    id="electronic_provider_name"
                    name="electronic_provider_name"
                    className="form-control"
                    value={form.electronic_provider_name}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="Nombre del proveedor elegido"
                    required
                  />
                </div>
              )}
            </div>

            {canManage && (
              <button
                type="submit"
                className="btn btn-primary mt-3"
              >
                {saving
                  ? "Guardando…"
                  : "Guardar configuración"}
              </button>
            )}
          </fieldset>

          {error && (
            <div
              className="alert alert-danger mt-3"
              role="alert"
            >
              {error}
            </div>
          )}

          <p className="small text-muted mt-3 mb-0">
            Guardar estas preferencias no habilita la transmisión.
            La conexión seguirá pendiente hasta implementar y
            verificar la integración seleccionada.
          </p>
        </form>
      )}
    </section>
  );
}